import * as Sentry from '@sentry/nextjs'
import { captureException } from '@sentry/nextjs'
import { isNetworkError } from 'axios-retry'

import { ensureError, getResponseStatus } from 'shared/utils/errors'

const MAX_DEPTH = 20

function errorToPlainObject(err: Error, depth = 0): Record<string, unknown> {
  if (depth >= MAX_DEPTH) return {}

  return {
    name: err.name,
    message: err.message,
    ...(err.stack && { stack: err.stack }),
    ...(err.cause !== undefined && {
      cause:
        err.cause instanceof Error
          ? errorToPlainObject(err.cause, depth + 1)
          : err.cause
    })
  }
}

/** Safe serialization for logging (Error + circular refs don't JSON.stringify well). */
function serializeForLog(value: unknown): string {
  if (value === null) return 'null'
  if (value === undefined) return 'undefined'
  if (typeof value === 'string') return value
  if (
    typeof value === 'number' ||
    typeof value === 'boolean' ||
    typeof value === 'bigint'
  )
    return String(value)
  // Same output as String(value) for these two.
  if (typeof value === 'symbol' || typeof value === 'function')
    return value.toString()
  if (value instanceof Error)
    return JSON.stringify(errorToPlainObject(value), null, 2)
  try {
    return JSON.stringify(value, null, 2)
  } catch {
    // Circular reference or similar — last-resort tag, not accidental default stringification.
    return Object.prototype.toString.call(value)
  }
}

export interface ErrorContext {
  context?: string
  userId?: string

  tags?: Record<string, string>
  extra?: Record<string, unknown>
  level?: 'debug' | 'info' | 'warning' | 'error' | 'fatal'
}

export const reportSentryError = (
  error: unknown,
  context?: string | ErrorContext
) => {
  if (isNetworkError(error)) {
    console.error('Network error', error)

    return
  }

  const errorContext: ErrorContext =
    typeof context === 'string' ? { context } : context || {}

  const {
    context: contextString,
    userId,
    tags = {},
    extra = {},
    level = errorContext.level || 'error'
  } = errorContext

  // Never hand a non-Error to Sentry — it produces useless titles/grouping
  // ("Object captured as exception with keys: ..."). The original value stays
  // available via error.cause and the error_details context below.
  const capturedError = ensureError(error, contextString)

  // Use Sentry's withScope to add context
  return Sentry.withScope(scope => {
    try {
      // Set the error level
      scope.setLevel(level)

      // Add context string
      if (contextString) {
        scope.setContext('error_context', { message: contextString })
      }

      // Add user information
      if (userId) {
        scope.setUser({ id: userId })
      }

      // Add generic debugging tags
      scope.setTag(
        'error_type',
        error instanceof Error ? error.constructor.name : typeof error
      )

      scope.setTag(
        'platform',
        typeof window !== 'undefined' ? 'client' : 'server'
      )

      // Add custom tags
      Object.entries(tags).forEach(([key, value]) => {
        scope.setTag(key, value)
      })

      // Add generic debugging context
      scope.setContext('debug_info', {
        timestamp: new Date().toISOString(),
        user_agent:
          typeof window !== 'undefined' ? window.navigator.userAgent : 'server',
        url: typeof window !== 'undefined' ? window.location.href : 'server',
        referrer: typeof window !== 'undefined' ? document.referrer : 'server',
        viewport:
          typeof window !== 'undefined'
            ? {
                width: window.innerWidth,
                height: window.innerHeight
              }
            : null
      })

      // Add error details context
      if (error instanceof Error) {
        scope.setContext('error_details', {
          name: error.name,
          message: error.message,
          stack: error.stack,
          cause: error.cause
        })
      } else {
        scope.setContext('error_details', {
          type: typeof error,
          value: String(error),
          stringified: serializeForLog(error)
        })
      }

      // Keep API response diagnostics (status/endpoint/body) that used to be
      // visible when raw {response} objects were serialized — Error instances
      // only carry name/message/stack by default.
      const responseStatus = getResponseStatus(error)

      if (responseStatus !== undefined) {
        const withResponse = error as {
          endpoint?: unknown
          response?: { data?: unknown; statusText?: unknown }
        }

        scope.setContext('api_response', {
          status: responseStatus,
          statusText:
            typeof withResponse.response?.statusText === 'string'
              ? withResponse.response.statusText
              : '',
          endpoint:
            typeof withResponse.endpoint === 'string'
              ? withResponse.endpoint
              : undefined,
          data: serializeForLog(withResponse.response?.data).slice(0, 2000)
        })
      }

      // Add custom extra data
      Object.entries(extra).forEach(([key, value]) => {
        scope.setExtra(key, value)
      })

      console.error('SE:', serializeForLog(error))
    } catch (scopeError) {
      // If context setup fails, still log and capture the original error
      console.error('SE:', serializeForLog(error))
      console.warn('Sentry scope setup failed:', scopeError)
    }

    return captureException(capturedError)
  })
}

export const setUserContext = (user: {
  id?: string
  email?: string
  username?: string
}) => {
  Sentry.setUser(user)
}
